import React from 'react';
import { Link } from 'react-router-dom';

import styles from './CtaButton.module.css';

/** Props for {@link CtaButton}. */
export interface CtaButtonProps {
  /**
   * Destination. Internal routes (`/product/ai`) render a router `<Link>`,
   * same-page anchors (`#waitlist`) and external URLs render an `<a>`.
   */
  readonly href: string;
  /** `primary` = filled glow button, `secondary` = ghost outline. */
  readonly variant?: 'primary' | 'secondary';
  /** `lg` for hero CTAs, `md` (default) everywhere else. */
  readonly size?: 'md' | 'lg';
  readonly className?: string;
  readonly children: React.ReactNode;
}

function classesFor(
  variant: 'primary' | 'secondary',
  size: 'md' | 'lg',
  className?: string
): string {
  return [
    styles['button'],
    variant === 'primary' ? styles['primary'] : styles['secondary'],
    size === 'lg' ? styles['lg'] : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Marketing call-to-action button.
 *
 * @remarks
 * Primary variant is reserved for "Join the waitlist"; secondary for
 * "Request early access" (see OUTLINE.md §2.7 — max 3 CTAs per page).
 */
export function CtaButton({
  href,
  variant = 'primary',
  size = 'md',
  className,
  children,
}: CtaButtonProps): React.ReactElement {
  const cls = classesFor(variant, size, className);
  const isInternalRoute = href.startsWith('/') && !href.startsWith('//');

  if (isInternalRoute) {
    return (
      <Link to={href} className={cls}>
        {children}
      </Link>
    );
  }

  const isExternal = /^https?:\/\//.test(href);
  return (
    <a
      href={href}
      className={cls}
      {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </a>
  );
}
